import { useCallback, useMemo } from "react"
import { Box, FormControlLabel, Switch, TextField, Typography } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { ConstraintConfig, ConstraintInput } from "connected-components/optimize-experiments/configuration-sections"

import "./style.scss"

interface ConstraintField<TValue> {
  state: {
    value: TValue | null
    meta?: {
      errors?: ({ message?: string } | undefined)[]
    }
  }
  handleChange: (updater: (TValue | null) | ((prev: TValue | null) => (TValue | null))) => void
  handleBlur: () => void
}

interface ConstraintSwitchProps<TValue> {
  field: ConstraintField<TValue>
  config: ConstraintConfig<TValue>
  disabled?: boolean
}

export default function ConstraintSwitch<TValue>({ field, config, disabled }: ConstraintSwitchProps<TValue>) {
  const { label, tooltip, defaultValue, inputs = [] } = config

  const isEnabled = !disabled && field.state.value !== null
  const toggleEnabled = useCallback((checked: boolean) => {
    if (!checked) {
      field.handleChange(null)
      return
    }

    if (inputs.length === 0) {
      field.handleChange(true as TValue)
    } else {
      field.handleChange(defaultValue)
    }
  }, [field, inputs, defaultValue])

  const handleInputChange = useCallback((fieldKey: string, value: string) => {
    if (inputs.length === 1) {
      field.handleChange(value as TValue)
    } else {
      const currentValue = field.state.value
      if (typeof currentValue !== "object" || currentValue === null) {
        field.handleChange({
          [fieldKey]: value,
        } as TValue)
      } else {
        field.handleChange({
          ...currentValue,
          [fieldKey]: value,
        } as TValue)
      }
    }
  }, [field, inputs])

  const getValue = useCallback((input: ConstraintInput) => {
    if (input.fieldKey === "shortMarketValue") {
      const currentValue = field.state.value as { longMarketValue: string } | null
      const lmv = parseFloat(currentValue?.longMarketValue ?? "")
      if (isNaN(lmv)) {
        return "-"
      }
      return parseFloat((100 - lmv).toFixed(2))
    }
    if (inputs.length === 1) {
      return field.state.value
    } else {
      const currentValue = field.state.value as Record<string, string>
      return currentValue?.[input.fieldKey]
    }
  }, [field.state.value, inputs.length])

  const constraintError = useMemo(() => {
    if (!isEnabled) {
      return undefined
    }

    const errors = field.state.meta?.errors
    if (!errors || errors.length === 0) return undefined

    const firstError = errors.find((error) => error?.message)
    return firstError?.message
  }, [field.state.meta, isEnabled])

  const hasError = !!constraintError

  return (
    <Box className="components_constraint-switch">
      <Box className="constraint-switch-label">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={isEnabled}
              disabled={disabled}
              onChange={(e) => toggleEnabled(e.target.checked)}
            />
          }
          label={label}
        />
        {tooltip && (
          <OPTooltip title={tooltip}>
            <InfoOutlined fontSize="small" />
          </OPTooltip>
        )}
        {hasError && (
          <Typography variant="caption" className="constraint-switch-error">
            {constraintError}
          </Typography>
        )}
      </Box>

      {isEnabled && (
        <Box className="constraint-switch-content">
          {inputs.map((input) => (
            <Box key={input.fieldKey} className="constraint-switch-input">
              <Box className="input-row">
                <TextField
                  variant="standard"
                  type="number"
                  className="constraint-switch-input-field"
                  value={getValue(input)}
                  onChange={(e) => handleInputChange(input.fieldKey, e.target.value)}
                  disabled={!!input.disabled}
                />
                <Typography variant="body2" className="constraint-switch-input-unit">
                  {input.unit}
                </Typography>
              </Box>
              {
                input.label && (
                  <Typography variant="caption" color="text.secondary" className="constraints-switch-input-label">
                    {input.label}
                  </Typography>
                )
              }
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
